import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight, Check, Home, Users, Bell, FileText, Shield, Clock, Wrench, Building } from 'lucide-react'
import { Footer } from '../components/SharedLayout'

// ============================================
// DESIGN TOKENS
// ============================================
const colors = {
  // Primary Palette
  teal: '#145F6B',
  tealDark: '#083B3A',
  copper: '#9B775D',
  cream: '#F6F6F1',
  
  // Dark Theme
  darkBase: '#0b1a1a',
  darkGradientFrom: '#0a1515',
  darkGradientVia: '#0d1e1e',
  darkGradientTo: '#081212',
  
  // Greyscale
  grey50: '#fafafa',
  grey100: '#f5f5f5',
  grey200: '#e5e5e5',
  grey300: '#d4d4d4',
  grey400: '#a3a3a3',
  grey500: '#737373',
  grey600: '#525252',
  grey700: '#404040',
  grey800: '#262626',
  grey900: '#171717',
}

const typography = {
  heroHeadline: 'clamp(3.5rem, 9vw, 7rem)',
  sectionHeadline: 'clamp(2.5rem, 5vw, 4rem)',
  cardHeadline: 'clamp(2rem, 4vw, 3rem)',
  subheadline: 'clamp(1.75rem, 3vw, 2.5rem)',
  body: '1rem',       // 16px
  bodyLarge: '1.125rem', // 18px
  small: '0.875rem',  // 14px
  label: '0.6875rem', // 11px
}

const spacing = {
  sectionPadding: 'py-32 lg:py-48',
  containerPadding: 'px-6 lg:px-12',
  maxWidth: '1400px',
}

const ease = [0.16, 1, 0.3, 1] as const

// ============================================
// PAGE COMPONENT
// ============================================
const StyleGuidePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-white/10 sticky top-0 z-50" style={{ backgroundColor: '#0b1a1a' }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="text-white/60 hover:text-white font-medium transition-colors">← Back to Home</Link>
            <h1 className="text-[14px] tracking-[0.2em] uppercase text-white/40">Design System</h1>
          </div>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="py-20 lg:py-32" style={{ backgroundColor: colors.cream }}>
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <p className="text-[11px] tracking-[0.4em] uppercase text-grey-400 mb-6">Hudson Design System</p>
            <h1 className="font-serif text-grey-900 font-light" style={{ fontSize: typography.heroHeadline, lineHeight: 0.9, letterSpacing: '-0.02em' }}>
              Style Guide
            </h1>
            <p className="mt-8 text-xl text-grey-500 max-w-2xl">
              A comprehensive reference for the Hudson brand's visual language, 
              typography, colors, and component patterns.
            </p>
          </div>
        </section>

        {/* Color Palette */}
        <section className="py-20 lg:py-32 bg-white">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <div className="flex items-center gap-4 mb-12">
              <div className="w-8 h-[1px] bg-grey-300" />
              <span className="text-[11px] tracking-[0.4em] uppercase text-grey-400">Color Palette</span>
            </div>
            
            <h2 className="font-serif text-grey-900 font-light mb-16" style={{ fontSize: typography.cardHeadline, lineHeight: 1, letterSpacing: '-0.02em' }}>
              Brand Colors
            </h2>
            
            {/* Primary Colors */}
            <div className="mb-16">
              <h3 className="text-[13px] tracking-[0.2em] uppercase text-grey-400 mb-6">Primary</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { name: 'Teal', value: colors.teal, textColor: 'white' },
                  { name: 'Teal Dark', value: colors.tealDark, textColor: 'white' },
                  { name: 'Copper', value: colors.copper, textColor: 'white' },
                  { name: 'Cream', value: colors.cream, textColor: colors.grey900 },
                ].map(c => (
                  <div key={c.name} className="aspect-[3/2] rounded-lg p-6 flex flex-col justify-end" style={{ backgroundColor: c.value }}>
                    <p className="font-medium" style={{ color: c.textColor }}>{c.name}</p>
                    <p className="text-[13px] opacity-70" style={{ color: c.textColor }}>{c.value}</p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Dark Theme */}
            <div className="mb-16">
              <h3 className="text-[13px] tracking-[0.2em] uppercase text-grey-400 mb-6">Dark Theme</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { name: 'Dark Base', value: colors.darkBase },
                  { name: 'Gradient From', value: colors.darkGradientFrom },
                  { name: 'Gradient Via', value: colors.darkGradientVia },
                  { name: 'Gradient To', value: colors.darkGradientTo },
                ].map(c => (
                  <div key={c.name} className="aspect-[3/2] rounded-lg p-6 flex flex-col justify-end" style={{ backgroundColor: c.value }}>
                    <p className="font-medium text-white">{c.name}</p>
                    <p className="text-[13px] text-white/70">{c.value}</p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Greyscale */}
            <div>
              <h3 className="text-[13px] tracking-[0.2em] uppercase text-grey-400 mb-6">Greyscale</h3>
              <div className="grid grid-cols-5 md:grid-cols-10 gap-2">
                {[
                  { name: '50', value: colors.grey50 },
                  { name: '100', value: colors.grey100 },
                  { name: '200', value: colors.grey200 },
                  { name: '300', value: colors.grey300 },
                  { name: '400', value: colors.grey400 },
                  { name: '500', value: colors.grey500 },
                  { name: '600', value: colors.grey600 },
                  { name: '700', value: colors.grey700 },
                  { name: '800', value: colors.grey800 },
                  { name: '900', value: colors.grey900 },
                ].map((c, i) => (
                  <div key={c.name} className="aspect-square rounded-lg flex flex-col items-center justify-center" style={{ backgroundColor: c.value }}>
                    <p className={`text-[11px] font-medium ${i < 5 ? 'text-grey-900' : 'text-white'}`}>{c.name}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Typography */}
        <section className="py-20 lg:py-32" style={{ backgroundColor: colors.cream }}>
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <div className="flex items-center gap-4 mb-12">
              <div className="w-8 h-[1px] bg-grey-300" />
              <span className="text-[11px] tracking-[0.4em] uppercase text-grey-400">Typography</span>
            </div>
            
            <h2 className="font-serif text-grey-900 font-light mb-16" style={{ fontSize: typography.cardHeadline, lineHeight: 1, letterSpacing: '-0.02em' }}>
              Type Scale
            </h2>
            
            <div className="space-y-12">
              {/* Hero Headline */}
              <div className="border-b border-grey-200 pb-12">
                <div className="flex items-baseline justify-between mb-4">
                  <span className="text-[11px] tracking-[0.2em] uppercase text-grey-400">Hero Headline</span>
                  <span className="text-[13px] text-grey-400 font-mono">{typography.heroHeadline}</span>
                </div>
                <p className="font-serif text-grey-900 font-light" style={{ fontSize: typography.heroHeadline, lineHeight: 0.9, letterSpacing: '-0.02em' }}>
                  Live in your home
                </p>
                <p className="font-serif text-grey-900 font-light italic mt-2" style={{ fontSize: typography.heroHeadline, lineHeight: 0.9, letterSpacing: '-0.02em', color: colors.copper }}>
                  like a guest in ours.™
                </p>
              </div>
              
              {/* Section Headline */}
              <div className="border-b border-grey-200 pb-12">
                <div className="flex items-baseline justify-between mb-4">
                  <span className="text-[11px] tracking-[0.2em] uppercase text-grey-400">Section Headline</span>
                  <span className="text-[13px] text-grey-400 font-mono">{typography.sectionHeadline}</span>
                </div>
                <p className="font-serif text-grey-900 font-light" style={{ fontSize: typography.sectionHeadline, lineHeight: 0.95, letterSpacing: '-0.02em' }}>
                  Two tiers. <span className="italic" style={{ color: colors.teal }}>One philosophy.</span>
                </p>
              </div>
              
              {/* Card Headline */}
              <div className="border-b border-grey-200 pb-12">
                <div className="flex items-baseline justify-between mb-4">
                  <span className="text-[11px] tracking-[0.2em] uppercase text-grey-400">Card Headline</span>
                  <span className="text-[13px] text-grey-400 font-mono">{typography.cardHeadline}</span>
                </div>
                <p className="font-serif text-grey-900 font-light" style={{ fontSize: typography.cardHeadline, lineHeight: 1, letterSpacing: '-0.02em' }}>
                  Command central for your home.
                </p>
              </div>
              
              {/* Body Text */}
              <div className="border-b border-grey-200 pb-12">
                <div className="flex items-baseline justify-between mb-4">
                  <span className="text-[11px] tracking-[0.2em] uppercase text-grey-400">Body Large</span>
                  <span className="text-[13px] text-grey-400 font-mono">18px / 1.7</span>
                </div>
                <p className="text-lg leading-relaxed text-grey-500 max-w-2xl">
                  Hotel-level care for your home. A complimentary app to manage everything. 
                  A dedicated professional when you desire the full white-glove experience.
                </p>
              </div>
              
              {/* Label */}
              <div>
                <div className="flex items-baseline justify-between mb-4">
                  <span className="text-[11px] tracking-[0.2em] uppercase text-grey-400">Section Label</span>
                  <span className="text-[13px] text-grey-400 font-mono">11px / tracking: 0.4em / uppercase</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-8 h-[1px]" style={{ backgroundColor: colors.copper }} />
                  <span className="text-[11px] tracking-[0.4em] uppercase" style={{ color: colors.copper }}>
                    Private Home Management
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Components */}
        <section className="py-20 lg:py-32 bg-white">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <div className="flex items-center gap-4 mb-12">
              <div className="w-8 h-[1px] bg-grey-300" />
              <span className="text-[11px] tracking-[0.4em] uppercase text-grey-400">Components</span>
            </div>
            
            <h2 className="font-serif text-grey-900 font-light mb-16" style={{ fontSize: typography.cardHeadline, lineHeight: 1, letterSpacing: '-0.02em' }}>
              UI Elements
            </h2>
            
            {/* Buttons */}
            <div className="mb-20">
              <h3 className="text-[13px] tracking-[0.2em] uppercase text-grey-400 mb-8">Buttons</h3>
              <div className="flex flex-wrap gap-4">
                <button className="group inline-flex items-center gap-3 bg-grey-900 text-white px-8 py-5 text-[15px] font-medium tracking-wide transition-all duration-500 hover:bg-grey-800">
                  Primary Button
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-500" />
                </button>
                
                <button className="group inline-flex items-center gap-3 text-white px-8 py-5 text-[15px] font-medium tracking-wide transition-all duration-500 hover:opacity-90" style={{ backgroundColor: colors.copper }}>
                  Accent Button
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-500" />
                </button>
                
                <button className="group inline-flex items-center gap-3 border border-grey-200 text-grey-900 px-8 py-5 text-[15px] font-medium tracking-wide transition-all duration-500 hover:bg-grey-50">
                  Secondary Button
                </button>
                
                <button className="group inline-flex items-center gap-3 border border-white/20 text-white px-8 py-5 text-[15px] font-medium tracking-wide transition-all duration-500 hover:bg-white/5 bg-grey-900">
                  Ghost (Dark)
                </button>
              </div>
            </div>
            
            {/* Badges */}
            <div className="mb-20">
              <h3 className="text-[13px] tracking-[0.2em] uppercase text-grey-400 mb-8">Badges</h3>
              <div className="flex flex-wrap gap-4">
                <span className="text-[10px] font-medium tracking-wider uppercase px-3 py-1.5 bg-emerald-50 text-emerald-600">
                  Complimentary
                </span>
                <span className="text-[10px] font-medium tracking-wider uppercase px-3 py-1.5" style={{ backgroundColor: `${colors.copper}30`, color: colors.copper }}>
                  $399/mo
                </span>
                <span className="text-[11px] tracking-[0.3em] uppercase text-grey-400">
                  Level One
                </span>
              </div>
            </div>
            
            {/* Cards */}
            <div className="mb-20">
              <h3 className="text-[13px] tracking-[0.2em] uppercase text-grey-400 mb-8">Cards</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {/* Light Card */}
                <div className="bg-white p-10 shadow-sm border border-grey-100">
                  <div className="flex items-start justify-between mb-8">
                    <div>
                      <span className="text-[11px] tracking-[0.3em] uppercase text-grey-400 block mb-2">Level One</span>
                      <h3 className="font-serif text-2xl text-grey-900 font-light">The App</h3>
                    </div>
                    <span className="text-[11px] tracking-[0.2em] uppercase px-4 py-2 bg-grey-50 text-grey-500">Complimentary</span>
                  </div>
                  <p className="text-[15px] text-grey-500 leading-relaxed mb-8">
                    Complete visibility into every aspect of your home. Manage properties, 
                    book vetted vendors on-demand.
                  </p>
                  <div className="space-y-3">
                    {['Multi-property dashboard', 'On-demand vendor marketplace'].map((f, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: colors.teal }} />
                        <span className="text-[14px] text-grey-600">{f}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Dark Card */}
                <div className="p-10 bg-[#0b1a1a]">
                  <div className="flex items-start justify-between mb-8">
                    <div>
                      <span className="text-[11px] tracking-[0.3em] uppercase text-white/30 block mb-2">Level Two</span>
                      <h3 className="font-serif text-2xl text-white font-light">Membership</h3>
                    </div>
                    <span className="text-[11px] tracking-[0.2em] uppercase px-4 py-2" style={{ backgroundColor: `${colors.copper}20`, color: colors.copper }}>$399/mo</span>
                  </div>
                  <p className="text-[15px] text-white/50 leading-relaxed mb-8">
                    Full delegation. Your own dedicated Hudson professional who knows your home 
                    intimately.
                  </p>
                  <div className="space-y-3">
                    {['Dedicated home professional', 'Monthly in-home visits'].map((f, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: colors.copper }} />
                        <span className="text-[14px] text-white/60">{f}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Feature Icons */}
            <div>
              <h3 className="text-[13px] tracking-[0.2em] uppercase text-grey-400 mb-8">Feature Icons</h3>
              <div className="grid grid-cols-4 md:grid-cols-8 gap-4">
                {[Home, Users, Bell, FileText, Shield, Clock, Wrench, Building].map((Icon, i) => (
                  <div key={i} className="aspect-square rounded-xl flex items-center justify-center" style={{ backgroundColor: `${i % 2 === 0 ? colors.teal : colors.copper}15` }}>
                    <Icon className="w-6 h-6" style={{ color: i % 2 === 0 ? colors.teal : colors.copper }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Spacing & Layout */}
        <section className="py-20 lg:py-32" style={{ backgroundColor: colors.cream }}>
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <div className="flex items-center gap-4 mb-12">
              <div className="w-8 h-[1px] bg-grey-300" />
              <span className="text-[11px] tracking-[0.4em] uppercase text-grey-400">Spacing & Layout</span>
            </div>
            
            <h2 className="font-serif text-grey-900 font-light mb-16" style={{ fontSize: typography.cardHeadline, lineHeight: 1, letterSpacing: '-0.02em' }}>
              Layout System
            </h2>
            
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h3 className="text-lg font-medium text-grey-900 mb-6">Container</h3>
                <div className="space-y-4">
                  <div className="flex justify-between py-3 border-b border-grey-200">
                    <span className="text-[14px] text-grey-600">Max Width</span>
                    <span className="text-[14px] font-mono text-grey-400">1400px</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-grey-200">
                    <span className="text-[14px] text-grey-600">Horizontal Padding</span>
                    <span className="text-[14px] font-mono text-grey-400">px-6 lg:px-12</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-grey-900 mb-6">Sections</h3>
                <div className="space-y-4">
                  <div className="flex justify-between py-3 border-b border-grey-200">
                    <span className="text-[14px] text-grey-600">Vertical Padding</span>
                    <span className="text-[14px] font-mono text-grey-400">py-32 lg:py-48</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-grey-200">
                    <span className="text-[14px] text-grey-600">Grid Gap</span>
                    <span className="text-[14px] font-mono text-grey-400">gap-6 to gap-16</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Animation */}
        <section className="py-20 lg:py-32 bg-white">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <div className="flex items-center gap-4 mb-12">
              <div className="w-8 h-[1px] bg-grey-300" />
              <span className="text-[11px] tracking-[0.4em] uppercase text-grey-400">Motion</span>
            </div>
            
            <h2 className="font-serif text-grey-900 font-light mb-16" style={{ fontSize: typography.cardHeadline, lineHeight: 1, letterSpacing: '-0.02em' }}>
              Animation Principles
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="border-t-2 pt-8" style={{ borderColor: colors.teal }}>
                <h3 className="text-lg font-medium text-grey-900 mb-2">Easing</h3>
                <p className="text-[14px] font-mono text-grey-400 mb-4">[0.16, 1, 0.3, 1]</p>
                <p className="text-[15px] text-grey-500">Premium cubic-bezier curve for smooth, decelerating animations.</p>
              </div>
              
              <div className="border-t-2 pt-8" style={{ borderColor: colors.teal }}>
                <h3 className="text-lg font-medium text-grey-900 mb-2">Duration</h3>
                <p className="text-[14px] font-mono text-grey-400 mb-4">300-800ms</p>
                <p className="text-[15px] text-grey-500">Hovers at 300ms, reveals at 600-800ms, parallax continuous.</p>
              </div>
              
              <div className="border-t-2 pt-8" style={{ borderColor: colors.teal }}>
                <h3 className="text-lg font-medium text-grey-900 mb-2">Stagger</h3>
                <p className="text-[14px] font-mono text-grey-400 mb-4">0.1-0.15s delay</p>
                <p className="text-[15px] text-grey-500">Sequential reveals with consistent delay offsets.</p>
              </div>
            </div>
            
            {/* Animation Demo */}
            <div className="mt-16 p-12 bg-grey-50 rounded-xl">
              <p className="text-[11px] tracking-[0.3em] uppercase text-grey-400 mb-8">Hover Animation Demo</p>
              <div className="flex gap-6">
                <motion.button
                  whileHover={{ x: 4 }}
                  transition={{ duration: 0.3, ease }}
                  className="group inline-flex items-center gap-3 bg-grey-900 text-white px-8 py-5 text-[15px] font-medium"
                >
                  Hover Me
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                </motion.button>
                
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3, ease }}
                  className="w-32 h-16 bg-grey-200 rounded-lg flex items-center justify-center cursor-pointer"
                >
                  <span className="text-[14px] text-grey-500">Scale</span>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* Dark Section Example */}
        <section className="py-20 lg:py-32 relative overflow-hidden bg-[#0b1a1a]">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0a1515] via-[#0d1e1e] to-[#081212]" />
          <div 
            className="absolute top-0 right-0 w-1/2 h-full opacity-20"
            style={{ background: `radial-gradient(ellipse 100% 80% at 100% 50%, ${colors.copper}40, transparent)` }}
          />
          
          <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
            <div className="flex items-center gap-4 mb-12">
              <div className="w-8 h-[1px]" style={{ backgroundColor: colors.copper }} />
              <span className="text-[11px] tracking-[0.4em] uppercase" style={{ color: colors.copper }}>Dark Section</span>
            </div>
            
            <h2 className="font-serif text-white font-light mb-8" style={{ fontSize: typography.cardHeadline, lineHeight: 1, letterSpacing: '-0.02em' }}>
              Dark theme with
              <br /><span className="italic" style={{ color: colors.copper }}>teal-tinted gradients.</span>
            </h2>
            
            <p className="text-lg text-white/50 max-w-2xl mb-12">
              Dark sections use a teal-tinted base (#0b1a1a) rather than pure black, 
              with subtle gradient overlays and copper/teal accent glows for depth.
            </p>
            
            <div className="grid md:grid-cols-3 gap-8">
              {['White/60 Body', 'White/40 Secondary', 'White/30 Muted'].map((label, i) => (
                <div key={i} className="border-l border-white/10 pl-6">
                  <p className={`text-[15px] ${i === 0 ? 'text-white/60' : i === 1 ? 'text-white/40' : 'text-white/30'}`}>
                    {label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}

export default StyleGuidePage

